import { getLogger } from 'loglevel';
import loadJs from './loadJs';

/* eslint-disable no-param-reassign, func-style */
/**
 * @see https://blog.teknkl.com/destyle-a-marketo-form/
 * @author Sanford Whiteman
 * @version v1.104
 * @license MIT License: This license must appear with all reproductions of this software.
 *
 * Create a completely barebones, user-styles-only Marketo form
 * by removing inline STYLE attributes and disabling STYLE and LINK elements
 */
function destyleMktoForm(formEl, moreStyles) {
  const arrayFrom = Function.prototype.call.bind(Array.prototype.slice);

  // Remove element styles from <form> and children
  const styledEls = arrayFrom(formEl.querySelectorAll('[style]')).concat(formEl);

  styledEls.forEach(el => {
    el.removeAttribute('style');
  });

  // Disable remote stylesheets and local <style>s
  const styleSheets = arrayFrom(document.styleSheets);

  styleSheets.forEach(ss => {
    if (
    // eslint-disable-next-line no-undef
      [ mktoForms2BaseStyle, mktoForms2ThemeStyle ].includes(ss.ownerNode) ||
        formEl.contains(ss.ownerNode)
    ) {
      ss.disabled = true;
    }
  });

  if (!moreStyles) {
    formEl.dataset.stylesReady = 'true';
  }
}
/* eslint-enable */

/**
 * Script to load and handle Marketo forms.
 */
export default class FormMarketo {
  constructor() {
    this.selector = '.c-form-marketo';
    this.selectorForm = '.c-form-marketo__form';
    this.logger = getLogger('c-form-marketo');
    this.logger.setLevel('warn');
    this.loadedForms = [];
  }

  bootstrap() {
    this.loadScripts().then(() => this.loadForms());
  }

  loadScripts() {
    this.logger.info('loadScripts', 'Loading Marketo form scripts');

    return Promise.all([
      loadJs(`https://${this.getBaseUrl()}/js/forms2/js/forms2.js`),
    ]).catch(error => {
      this.logger.warn('bootstrap', 'Unable to load Marketo form scripts', error);
    });
  }

  getBaseUrl() {
    const anyForm = document.querySelector(this.selectorForm);

    return anyForm.getAttribute('data-mkto-base-url') || 'app-sj01.marketo.com';
  }

  loadForms() {
    if (!window.MktoForms2) {
      this.logger.warn('loadForm', 'Unable to load form. Marketo Forms API script not available.');

      return false;
    }

    document.querySelectorAll(this.selector).forEach($el => {
      const $form = $el.querySelector('.c-form-marketo__form');

      if ($form) {
        this.loadForm($form);
      }
    });

    MktoForms2.whenReady(form => { // eslint-disable-line no-undef
      this.whenReady(form);
    });

    MktoForms2.whenRendered(form => { // eslint-disable-line no-undef
      this.whenRendered(form);
    });
  }

  loadForm($formEl) {
    this.logger.info('loadForm', $formEl);

    const baseUrl = $formEl.getAttribute('data-mkto-base-url');
    const formId = $formEl.getAttribute('data-mkto-form-id');
    const mumchkinId = $formEl.getAttribute('data-mkto-munchkin-id');

    // Only load form once. Since form is not accessible via formElement, but hangs global scope, all forms with same id on the page will act the same.
    if (!this.loadedForms.includes(formId)) {
      MktoForms2.loadForm(`//${baseUrl}`, mumchkinId, formId, form => { // eslint-disable-line no-undef
        form.onValidate(this.onValidate(form, $formEl));
        form.onSubmit(this.onSubmit($formEl));
        form.onSuccess(this.onSuccess(form, $formEl));
      });

      this.loadedForms.push(formId);
    }
  }

  destyleForm(form) { // eslint-disable-line class-methods-use-this
    const formEl = form.getFormElem()[0];
    const includeStyle = formEl.getAttribute('data-mkto-include-styles');

    if (includeStyle !== '1') {
      destyleMktoForm(formEl);
    }
  }

  whenReady(form) {
    this.logger.info('whenReady', form);
  }

  whenRendered(form) {
    this.logger.info('whenRendered', form);
    this.destyleForm(form);
  }

  onValidate(form, $formEl) {
    return status => {
      this.logger.info('onValidate', status, $formEl);
    };
  }

  onSubmit($formEl) {
    return form => {
      this.logger.info('onSubmit', form, $formEl);
    };
  }

  onSuccess(form, $formEl) {
    return (data, url) => {
      this.logger.info('onSuccess', data, url, form);
      this.handelRedirect($formEl);
    };
  }

  handelRedirect($formEl) { // eslint-disable-line class-methods-use-this
    const redirectUrl = $formEl.getAttribute('data-redirect-url');

    if (redirectUrl) {
      window.location.href = redirectUrl;

      return false;
    }
  }
}

